import * as React from "react";

import type {
    ApolloGqlError,
    ApolloMutationOptions,
    ApolloResult,
    AsyncApolloMutationResult,
} from "@khan/data-gql-apollo";
import type {GqlOperation} from "@khan/data-gql-core";

import ApolloMutationCore from "./internal/apollo-mutation-core.ts";
import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "./with-apollo.tsx";

// @ts-expect-error - TS2344
type OwnProps<TData, TVariables> = ApolloMutationOptions<TData, TVariables> & {
    // @ts-expect-error - TS2344 - Type 'TVariables' does not satisfy the constraint 'object'.
    mutation: GqlOperation<TData, TVariables>;
    children: (
        mutateFn: (
            // @ts-expect-error - TS2344
            options?: Partial<ApolloMutationOptions<TData, TVariables>>,
        ) => Promise<ApolloResult<TData> | null | undefined>,
        result: AsyncApolloMutationResult<TData>,
    ) => React.ReactNode;
    onCompleted?: (data?: TData) => void;
    onError?: (error: ApolloGqlError) => void;
};

type Props<TData, TVariables> = OwnProps<TData, TVariables> & WithApolloProps;

const ApolloMutationImpl = <
    TData extends Record<any, any>,
    TVariables extends Record<any, any>,
>({
    children,
    mutation,
    onCompleted,
    onError,
    gqlClient,
    gqlDefaultModule,
    ...propOptions
}: Props<TData, TVariables>): React.ReactElement => (
    /**
     * Unlike React Apollo, our version of ApolloMutation does not
     * duplicate mutate options in props. Instead, we have split the
     * implementation in two and we wrap the injected mutate function
     * with our props when it is called. This results in the same
     * behavior, but hopefully makes things a little easier to reason
     * about.
     *
     * In addition, our version knows about ApolloGqlClient and uses that
     * so it doesn't even know about Apollo either. :)
     */
    <ApolloMutationCore
        mutation={mutation}
        client={gqlClient}
        onCompleted={onCompleted}
        onError={onError}
    >
        {(mutateFn, result) =>
            children(
                (options) =>
                    mutateFn({
                        module: gqlDefaultModule,
                        ...propOptions,
                        ...options,
                    }),
                result,
            )
        }
    </ApolloMutationCore>
);

const ApolloMutationWithContext = withApollo(ApolloMutationImpl);
export default function <
    TData extends Record<any, any>,
    TVariables extends Record<any, any>,
>(props: WithoutApollo<Props<TData, TVariables>>): React.ReactElement {
    // We know that the generic escapes here, but I can't work out a way to
    // avoid that when using an HOC around a genericly parameterized component.
    // We could likely define HOCs to accommodate this but then we'd lose the
    // handling of default props from React.ElementConfig. I'm not sure there's
    // a way to define a new `React.ElementConfig` that would work, so for now
    // we suppress the error :(
    return <ApolloMutationWithContext {...props} />;
}
